import { useState, useEffect } from 'react';
import { useSuperQuery } from '@vadiun/react-hooks-legacy';
import { useClientInformationRepository } from '../services/clientInformationRepository';
import { PricePerClientFiltersFormType } from '../types/pricePerClient';
import {
    loadStateFromLocalStorage,
    saveStateToLocalStorage,
    PricePerClientColumnVisibility,
    getDefaultSort,
    getDefaultColumnVisibility,
    getDefaultFilters
} from '../utils/pricePerClientStorage';


export { PricePerClientColumnLabels, getDefaultFilters } from '../utils/pricePerClientStorage';

export const usePricePerClient = () => {
    const serviceReposity = useClientInformationRepository();
    const [state, setState] = useState(loadStateFromLocalStorage);

    useEffect(() => {
        saveStateToLocalStorage(state);
    }, [state]);

    const getPricePerClient = useSuperQuery(
        () => serviceReposity.getPricesPerClient(
            state.page,
            state.rowsPerPage,
            state.sort.sortBy,
            state.sort.sortDir,
            state.filters),
        { showSpinner: true },
        [
            state.filters,
            state.page,
            state.rowsPerPage,
            state.sort.sortBy,
            state.sort.sortDir,
            JSON.stringify(state.filters)
        ]
    );

    const onChangePage = (newPage: number) => {
        if (newPage !== state.page) {
            setState(prev => ({ ...prev, page: newPage }));
        }
    };

    const onFiltersChange = (filtersEntry: PricePerClientFiltersFormType) => {
        setState(prev => ({ ...prev, filters: filtersEntry, page: 1 }));
    };

    const onChangeRowsPerPage = (newRowsPerPage: number) => {
        setState(prev => ({ ...prev, rowsPerPage: newRowsPerPage, page: 1 }));
    };

    const onSortChange = (direction: string | null, sortBy: string) => {
        setState(prev => ({
            ...prev,
            sort: {
                sortDir: direction === 'desc' ? 'DESC' : 'ASC',
                sortBy
            },
            page: 1
        }));
    };

    const handleSearchChange = (search: string | null) => {
        setState(prev => ({ ...prev, filters: { ...prev.filters, search: search }, page: 1 }));
    };

    const setColumnVisibility = (newColumnVisibility: PricePerClientColumnVisibility) => {
        setState(prev => ({ ...prev, columnVisibility: newColumnVisibility }));
    };

    const resetState = () => {
        setState({
            page: 1,
            rowsPerPage: 10,
            sort: getDefaultSort(),
            filters: getDefaultFilters(),
            columnVisibility: getDefaultColumnVisibility()
        });
    };

    return {
        pricePerClient: {
            data: getPricePerClient.data?.data || [],
            total: getPricePerClient.data?.total || 0,
            isLoading: getPricePerClient.isLoading,
            error: getPricePerClient.error,
            serverSideOptions: {
                page: state.page - 1,
                count: getPricePerClient.data?.total || 0,
                rowsPerPage: state.rowsPerPage,
                sortOrder: {
                    name: state.sort.sortBy,
                    direction: state.sort.sortDir.toLowerCase() as 'asc' | 'desc'
                }
            }
        },
        reload: getPricePerClient.reload,
        filters: state.filters,
        page: state.page,
        rowsPerPage: state.rowsPerPage,
        columnVisibility: state.columnVisibility,
        setColumnVisibility,
        resetState,
        onChangePage,
        onChangeRowsPerPage,
        onSortChange,
        onFiltersChange,
        handleSearchChange
    };
};